import { useEffect, useState } from "react";
import he from 'he'; // Decodifica entidades HTML (ñ, tildes, etc.) que vienen de la API

const INM = 'SSE';
const API_KEY = 'OXT0YCM6CC0CCMVUF44P58XTT';
const BASE_URL = 'https://xintel.com.ar/api/';
const RPPAGINA = 20;

// Promesa/cache compartida: el catálogo completo se trae una sola vez,
// sin importar cuántos componentes llamen a GetDatos/fetchAndFormatUbi/fetchAndFormatTipos.
let catalogoPromise = null;
let catalogoCache = null;

const decode = (valor) => (valor ? he.decode(String(valor).trim()) : '');

const textoOperacion = {
  V: 'VENTA',
  A: 'ALQUILER',
  M: 'VENTA Y ALQUILER',
  T: 'ALQUILER TEMPORARIO'
};

const textoOperacionFiltro = {
  V: 'ventas',
  M: 'ventas',
  A: 'alquileres',
  T: 'alquileres'
};

// in_esi=N (probado contra la API) filtra server-side a solo las fichas
// disponibles (excluye reservada/suspendida/vendida/alquilada).
// La API no siempre devuelve los mismos campos (p. ej. fichas.destacadas y
// fichas.ultimas vienen con menos datos que resultados.fichas), así que cada
// campo tiene un valor de respaldo en vez de quedar vacío.
const formatearFicha = (property, galeria = []) => {
  const tipo = property.tipo || '';
  // ambientes_num viene de la API como "3 Ambientes" (texto ya formateado),
  // no como número: nos quedamos solo con la cantidad.
  const ambientesNumero = parseInt(property.ambientes_num, 10);
  const ambientes = Number.isNaN(ambientesNumero) ? '' : String(ambientesNumero);
  const superficieTotal = property.in_sto || '';
  const ubicacion = decode(property.in_bar || property.in_loc || '');
  const operacionTexto = textoOperacion[property.in_ope] || '';
  const tituloApi = decode(property.titulo);
  const titulo = tituloApi || [tipo, operacionTexto && `en ${operacionTexto.toLowerCase()}`, ubicacion].filter(Boolean).join(' ');

  const imagenPrincipal = property.img_princ || galeria[0] || null;
  const metaDescripcion = `${tipo || 'Propiedad'} en ${operacionTexto || 'oferta'}${ambientes ? ` de ${ambientes}` : ''} en ${ubicacion || 'zona a consultar'}${superficieTotal ? ` con ${superficieTotal} m²` : ''}.`;

  return {
    nombre: titulo,
    titulo,
    imagen: imagenPrincipal,
    galeria: galeria.length ? galeria : (imagenPrincipal ? [imagenPrincipal] : []),
    direccion_completa: property.direccion_completa || '',
    precio: property.precio || 'Consultar',
    venta_precio: property.venta_precio || 0,
    venta_moneda: property.venta_moneda || '',
    alquiler_precio: property.alquiler_precio || 0,
    alquiler_moneda: property.alquiler_moneda || '',
    ambientes_2: property.ambientes || '',
    tipo,
    estado: property.estado || '',
    operacion: textoOperacionFiltro[property.in_ope] || '',
    ambientes,
    dormitorios: property.dormitorios || '',
    id: property.in_num,
    superficieTotal,
    in_sul: property.in_sul || '',
    sup_semicubierta: property.sup_semicubierta || '',
    descripcion: decode(property.in_obs || ''),
    zonaCiudad: property.zonaCiudad || '',
    ubicacion,
    localidad: decode(property.in_loc || ''),
    barrio: decode(property.in_bar || ''),
    in_cubierto: property.in_cub || '',
    disponibilidad: property.in_esi || '',
    baños: property.in_bao || '',
    cocheras: property.in_coc || property.garage || '',
    expensas: Number(property.in_exp) || 0,
    antiguedad: property.in_ant,
    metaDescripcion
  };
};

const construirQuery = (extra) => {
  const params = { json: 'resultados.fichas', inm: INM, apiK: API_KEY, ...extra };
  const limpio = Object.fromEntries(
    Object.entries(params).filter(([, valor]) => valor !== undefined && valor !== null && valor !== '')
  );
  return new URLSearchParams(limpio).toString();
};

const fetchJson = async (query) => {
  const response = await fetch(`${BASE_URL}?${query}`);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
};

const fetchPagina = (page) => fetchJson(construirQuery({
  tipo_operacion: '',
  tipo_inmueble: '',
  page,
  rppagina: RPPAGINA
}));

// Trae el catálogo completo en una sola tanda: pide la página 0, lee la
// cantidad total de páginas que informa la propia API (resultado.datos.paginas)
// y pide el resto en paralelo, en lugar de ir página por página hasta recibir
// una página vacía (lo que antes sumaba un pedido extra de más).
const fetchCatalogoCompleto = async () => {
  const primera = await fetchPagina(0);
  const totalPaginas = primera?.resultado?.datos?.paginas || 1;

  const resultados = [primera];
  if (totalPaginas > 1) {
    const restantes = await Promise.all(
      Array.from({ length: totalPaginas - 1 }, (_, i) => fetchPagina(i + 1))
    );
    resultados.push(...restantes);
  }

  const datos = [];
  const ubicaciones = [];
  const tipos = [];

  resultados.forEach((resultado) => {
    const fichas = resultado?.resultado?.fichas || [];
    fichas.forEach((property) => {
      const ficha = formatearFicha(property);

      if (ficha.ubicacion && !ubicaciones.includes(ficha.ubicacion)) {
        ubicaciones.push(ficha.ubicacion);
      }
      if (ficha.tipo && !tipos.includes(ficha.tipo)) {
        tipos.push(ficha.tipo);
      }

      datos.push(ficha);
    });
  });

  return { datos, ubicaciones, tipos };
};

const getCatalogo = () => {
  if (catalogoCache) {
    return Promise.resolve(catalogoCache);
  }

  if (!catalogoPromise) {
    catalogoPromise = fetchCatalogoCompleto()
      .then((resultado) => {
        catalogoCache = resultado;
        return resultado;
      })
      .catch((error) => {
        catalogoPromise = null; // permite reintentar en el próximo llamado
        throw error;
      });
  }

  return catalogoPromise;
};

const GetDatos = () => {
  const [loading, setLoading] = useState(!catalogoCache);
  const [datos, setDatos] = useState(catalogoCache?.datos || []);

  useEffect(() => {
    let cancelado = false;

    getCatalogo()
      .then((resultado) => {
        if (!cancelado) {
          setDatos(resultado.datos);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error al obtener los datos:', error);
        if (!cancelado) {
          setLoading(false);
        }
      });

    return () => {
      cancelado = true;
    };
  }, []);

  return { loading, datos };
};

const fetchAndFormatUbi = async () => {
  const { ubicaciones } = await getCatalogo();
  return ubicaciones.map((ubicacion) => ({ descripcion: ubicacion }));
};

const fetchAndFormatTipos = async () => {
  const { tipos } = await getCatalogo();
  return tipos.map((tipo) => ({ descripcion: tipo }));
};

// Lista de ambientes propia de la cuenta (parametros.ambientes), cacheada
// una sola vez. Códigos tipo "2A" / "3A" / "0A0D" con su etiqueta legible.
let ambientesPromise = null;

const fetchAndFormatAmbientes = () => {
  if (!ambientesPromise) {
    ambientesPromise = fetchJson(construirQuery({ json: 'parametros.ambientes' }))
      .then((resultado) => {
        const ambientes = resultado?.resultado?.ambientes || [];
        return ambientes
          .map((a) => ({
            codigo: a.ti_tip,
            descripcion: decode(a.ti_des),
            orden: Number(a.ti_amb) || 0
          }))
          .filter((a) => a.codigo)
          .sort((a, b) => a.orden - b.orden);
      })
      .catch((error) => {
        ambientesPromise = null;
        console.error('Error al obtener ambientes:', error);
        return [];
      });
  }
  return ambientesPromise;
};

// Búsqueda paginada contra la API con filtros server-side reales, en vez de
// traer todo el catálogo y filtrar en el navegador. "ubicacion" se manda por
// barrios1: probado contra la API, funciona tanto para barrios como para
// localidades de esta cuenta. Mandar sellocalidades y barrios1 juntos rompe
// el filtro (la API devuelve "cantidad": "" ), así que nunca van los dos a la vez.
const buscarFichas = async ({
  page = 0,
  rppagina = RPPAGINA,
  tipoOperacion = '',
  tipoInmueble = '',
  ubicacion = '',
  ambientes = '',
  moneda = '',
  precioMin = '',
  precioMax = '',
  texto = '',
  soloDisponibles = false
} = {}) => {
  const query = construirQuery({
    tipo_operacion: tipoOperacion,
    tipo_inmueble: tipoInmueble,
    barrios1: ubicacion,
    Ambientes: ambientes,
    moneda,
    valor_minimo: precioMin,
    valor_maximo: precioMax,
    codigo_ficha: texto,
    in_esi: soloDisponibles ? 'N' : '',
    page,
    rppagina
  });

  const resultado = await fetchJson(query);
  const fichas = resultado?.resultado?.fichas || [];
  const galerias = resultado?.resultado?.img || [];
  const meta = resultado?.resultado?.datos || {};

  return {
    datos: fichas.map((property, i) => formatearFicha(property, galerias[i] || [])),
    paginaActual: page,
    totalPaginas: Number(meta.paginas) || 0,
    totalResultados: Number(meta.cantidadFichas) || 0
  };
};

// Ficha individual completa (galería entera, no solo la imagen principal).
const fetchFichaPorId = async (id) => {
  const query = construirQuery({ in_num: id });
  const resultado = await fetchJson(query);
  const property = resultado?.resultado?.fichas?.[0];
  if (!property) {
    return null;
  }

  const galeria = resultado?.resultado?.img?.[0] || [];
  const caracteristicasRaw = resultado?.resultado?.caracteristicas?.[id];
  const caracteristicas = Array.isArray(caracteristicasRaw) ? caracteristicasRaw : [];

  return {
    ...formatearFicha(property, galeria),
    caracteristicas,
    superficieTerreno: property.in_sup || property.in_sto || '',
    superficieCubierta: property.in_cub || '',
    vendedorNombre: [decode(property.vendedor_nombre), decode(property.vendedor_apellido)].filter(Boolean).join(' '),
    vendedorTelefono: property.vendedor_telefono || '',
    vendedorCorreo: property.vendedor_correo || '',
    vendedorFoto: property.vendedor_foto
      ? `https://cdn-images.xintelweb.com/upload/profile/${property.vendedor_foto}`
      : ''
  };
};

// Normaliza la forma de respuesta de fichas.destacadas / fichas.ultimas
// (una usa "fichas", la otra "ficha") y aplica el mismo mapeo defensivo.
const mapearListaLigera = (resultado) => {
  const fichas = resultado?.resultado?.fichas || resultado?.resultado?.ficha || [];
  const galerias = resultado?.resultado?.img || [];
  return fichas.map((property, i) => formatearFicha(property, galerias[i] || []));
};

const fetchDestacadas = async (limit = 8) => {
  const query = construirQuery({ json: 'fichas.destacadas', limit });
  const resultado = await fetchJson(query);
  return mapearListaLigera(resultado);
};

const fetchUltimas = async (limit = 8) => {
  const query = construirQuery({ json: 'fichas.ultimas', limit });
  const resultado = await fetchJson(query);
  return mapearListaLigera(resultado);
};

export {
  GetDatos,
  fetchAndFormatUbi,
  fetchAndFormatTipos,
  fetchAndFormatAmbientes,
  buscarFichas,
  fetchFichaPorId,
  fetchDestacadas,
  fetchUltimas
};
